import { Button } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router-dom'

import { GapBox } from 'components/Box'
import Filter from 'components/Filter'
import { useCommonFormContextProps } from 'components/Form/Form/hooks/useCommonFormContextProps'
import { CommonFormContextProps } from 'components/Form/Form/types'
import { SaveButton } from 'components/Form/SaveButton/SaveButton'
import { Link } from 'components/Link'
import Paper from 'components/Paper'
import { TableHeader } from 'components/Table'
import {
  AdUnitListContext,
  AdUnitListContextType,
} from 'features/inventory/ad-unit/contexts/AdUnitListContext'
import { AdUnitTableForm } from 'features/inventory/ad-unit/forms/AdUnitTableForm'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { useAbility } from 'providers/casl'
import { useTableSearchParams } from 'providers/router/hooks/useTableSearchParams'
import { useUrlSearchParams } from 'providers/router/hooks/useUrlSearchParams'
import { ROUTES } from 'routes'

export const AdUnitList = (): React.JSX.Element => {
  const ability = useAbility()
  const { workspaceId } = useWorkspaceParam()
  const { t } = useTranslation('features/adUnit')
  const { filters, setFilters } = useTableSearchParams()
  const { urlParams } = useUrlSearchParams()

  const commonContextProps: CommonFormContextProps = useCommonFormContextProps({
    formId: `adunit-table-form`,
  })

  const contextProps: AdUnitListContextType = {
    ...commonContextProps,
    filters,
    setFilters,
  }

  const AddNew: React.JSX.Element = (
    <Link
      to={{
        pathname: generatePath(ROUTES.INVENTORY.AD_UNIT.ADD, { workspaceId }),
        search: urlParams,
      }}
    >
      <Button
        data-cy='ad-unit-button-add'
        variant='contained'
      >
        {t('list.add')}
      </Button>
    </Link>
  )

  const canCreate: boolean = ability.can('create', 'AdUnit')
  const canUpdate: boolean = ability.can('update', 'AdUnit')

  return (
    <Paper>
      <AdUnitListContext.Provider value={contextProps}>
        <AdUnitListContext.Consumer>
          {(ctxProps: AdUnitListContextType): React.JSX.Element => (
            <>
              <TableHeader
                actions={canCreate ? AddNew : undefined}
                filter={
                  <Filter
                    id='adUnitFilter'
                    onChange={ctxProps.setFilters}
                    placeholder={t('list.filter')}
                    values={ctxProps.filters}
                  />
                }
                save={
                  <SaveButton
                    {...ctxProps}
                    can={canUpdate}
                  />
                }
                title={t('list.title')}
              />

              <GapBox />
              <AdUnitTableForm />
            </>
          )}
        </AdUnitListContext.Consumer>
      </AdUnitListContext.Provider>
    </Paper>
  )
}
